import { useState } from 'react'
import { Box, Button, IconButton, InputAdornment, Stack, TextField, Typography } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { ValidationError } from 'yup'

import Modal from 'components/Modal'
import ActionModal from 'components/Modal/ActionModal'
import ChipStatus from 'packages/ui/ChipStatus'
import Switch from 'packages/ui/Switch'
import { createSsidNameSchema } from 'utils/ssid-name'
import { createSsidPassphraseSchema } from 'utils/ssid-passphrase'
import { generatePassphrase } from 'utils/generate-passphrase'

import { AUTHENTICATION_CONFIG, getAuthenticationTranslationKey } from './constants'
import { Ssid } from './types'

interface EditSsidValues {
  enabled: boolean
  name: string
  passphrase: string
}

interface EditSsidModalProps {
  loading: boolean
  ssid: Ssid
  onClose: () => void
  onSave: (values: EditSsidValues) => Promise<void>
}

const TEXT_FIELD_SX = {
  '& .MuiOutlinedInput-root': {
    borderRadius: '8px',
    fontSize: 12,
    height: 40
  },
  '& .MuiFormHelperText-root': {
    fontSize: 10,
    ml: 0
  }
}

const EditSsidModal = ({ loading, ssid, onClose, onSave }: EditSsidModalProps) => {
  const { t } = useTranslation()
  const [name, setName] = useState(ssid.name)
  const [passphrase, setPassphrase] = useState(ssid.passphrase || '')
  const [isNetworkEnabled, setIsNetworkEnabled] = useState(ssid.enabled)
  const [isPassphraseVisible, setIsPassphraseVisible] = useState(false)
  const [nameError, setNameError] = useState<string | null>(null)
  const [passphraseError, setPassphraseError] = useState<string | null>(null)
  const [isDisableConfirmationOpen, setIsDisableConfirmationOpen] = useState(false)

  const canEditPassphrase = ssid.auth_method === 'psk'

  const handleClose = () => {
    if (loading) return
    onClose()
  }

  const handleGeneratePassphrase = () => {
    setPassphrase(generatePassphrase())
    setPassphraseError(null)
  }

  const handleSubmit = async () => {
    await onSave({
      enabled: isNetworkEnabled,
      name,
      passphrase
    })
  }

  const handleSave = async () => {
    try {
      createSsidNameSchema(t).validateSync(name)
      setNameError(null)
    } catch (error) {
      if (error instanceof ValidationError) {
        setNameError(error.message)
        return
      }

      throw error
    }

    if (canEditPassphrase) {
      try {
        createSsidPassphraseSchema(t).validateSync(passphrase)
        setPassphraseError(null)
      } catch (error) {
        if (error instanceof ValidationError) {
          setPassphraseError(error.message)
          return
        }

        throw error
      }
    }

    if (ssid.enabled && !isNetworkEnabled) {
      setIsDisableConfirmationOpen(true)
      return
    }

    await handleSubmit()
  }

  const authenticationLabel = t(`ssid_monitoring.authentication.${getAuthenticationTranslationKey(ssid.auth_method)}`)
  const authenticationConfig = AUTHENTICATION_CONFIG[ssid.auth_method]

  if (isDisableConfirmationOpen) {
    return (
      <ActionModal
        cancelLabel={t('ui_elements.cancel')}
        confirmLabel={t('ssid_monitoring.actions.disable')}
        description={t('ssid_monitoring.disable_confirmation.description')}
        loading={loading}
        onClose={() => setIsDisableConfirmationOpen(false)}
        onConfirm={handleSubmit}
        title={t('ssid_monitoring.disable_confirmation.title', {
          name
        })}
      />
    )
  }

  return (
    <Modal
      icon={<i className="fa-light fa-pen" />}
      title={t('ssid_monitoring.edit_modal.heading')}
      maxWidth="440px"
      onClose={handleClose}
    >
      <ChipStatus
        status={t('ssid_monitoring.edit_modal.type', {
          type: authenticationLabel
        })}
        color={authenticationConfig.color}
        bgcolor={authenticationConfig.backgroundColor}
        height="24px"
        borderRadius="50px"
        sx={{ alignSelf: 'flex-start' }}
      />

      <Stack spacing={1}>
        <Typography sx={{ color: '#111111', fontSize: 12, fontWeight: 600 }}>
          {t('ssid_monitoring.edit_modal.name_label')}
        </Typography>
        <TextField
          autoFocus
          fullWidth
          value={name}
          disabled={loading}
          error={Boolean(nameError)}
          helperText={nameError || undefined}
          inputProps={{
            minLength: 3,
            maxLength: 32,
            'aria-label': t('ssid_monitoring.table.network')
          }}
          onChange={event => {
            setName(event.target.value)
            setNameError(null)
          }}
          onKeyDown={event => {
            if (event.key === 'Enter') {
              handleSave()
            }
          }}
          sx={TEXT_FIELD_SX}
        />
        <Stack direction="row" alignItems="flex-start" spacing={0.75}>
          <i
            aria-hidden="true"
            className="fa-light fa-triangle-exclamation"
            style={{ color: '#9A5B00', fontSize: 12, marginTop: 2 }}
          />
          <Typography sx={{ color: '#9A5B00', fontSize: 10, fontWeight: 400 }}>
            {t('ssid_monitoring.edit_modal.name_reconnection_warning')}
          </Typography>
        </Stack>
      </Stack>

      {canEditPassphrase && (
        <Stack spacing={1}>
          <Typography sx={{ color: '#111111', fontSize: 12, fontWeight: 600 }}>
            {t('ssid_monitoring.edit_modal.passphrase_label')}
          </Typography>
          <Stack direction="row" alignItems="flex-start" spacing={1}>
            <TextField
              fullWidth
              type={isPassphraseVisible ? 'text' : 'password'}
              value={passphrase}
              disabled={loading}
              error={Boolean(passphraseError)}
              helperText={passphraseError || undefined}
              inputProps={{
                minLength: 8,
                maxLength: 63,
                'aria-label': t('ssid_monitoring.table.passphrase')
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label={t(
                        isPassphraseVisible ? 'ssid_monitoring.passphrase.hide' : 'ssid_monitoring.passphrase.show'
                      )}
                      edge="end"
                      onClick={() => setIsPassphraseVisible(previousValue => !previousValue)}>
                      <i
                        aria-hidden="true"
                        className={`fa-light fa-${isPassphraseVisible ? 'eye-slash' : 'eye'}`}
                        style={{ fontSize: 13 }}
                      />
                    </IconButton>
                  </InputAdornment>
                )
              }}
              onChange={event => {
                setPassphrase(event.target.value)
                setPassphraseError(null)
              }}
              onKeyDown={event => {
                if (event.key === 'Enter') {
                  handleSave()
                }
              }}
              sx={TEXT_FIELD_SX}
            />
            <Button
              variant="outlined"
              disabled={loading}
              size="small"
              onClick={handleGeneratePassphrase}
              sx={{ flexShrink: 0, height: 40 }}>
              {t('ssid_monitoring.edit_modal.generate')}
            </Button>
          </Stack>
          <Stack direction="row" alignItems="flex-start" spacing={0.75}>
            <i
              aria-hidden="true"
              className="fa-light fa-triangle-exclamation"
              style={{ color: '#9A5B00', fontSize: 12, marginTop: 2 }}
            />
            <Typography sx={{ color: '#9A5B00', fontSize: 10, fontWeight: 400 }}>
              {t('ssid_monitoring.edit_modal.reconnection_warning')}
            </Typography>
          </Stack>
        </Stack>
      )}

      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ borderTop: '1px solid #EFEFEF', pt: 2 }}>
        <Box>
          <Typography sx={{ color: '#111111', fontSize: 12, fontWeight: 600 }}>
            {t('ssid_monitoring.edit_modal.network_enabled')}
          </Typography>
          <Typography sx={{ color: '#656565', fontSize: 10, fontWeight: 400 }}>
            {t('ssid_monitoring.edit_modal.network_enabled_helper')}
          </Typography>
        </Box>
        <Switch
          checked={isNetworkEnabled}
          disabled={loading}
          inputProps={{
            'aria-label': t('ssid_monitoring.edit_modal.network_enabled')
          }}
          onChange={event => setIsNetworkEnabled(event.target.checked)}
        />
      </Stack>

      <Stack direction="row" justifyContent="flex-end" spacing={1}>
        <Button variant="outlined" size="small" disabled={loading} onClick={handleClose}>
          {t('ui_elements.cancel')}
        </Button>
        <Button variant="contained" size="small" disabled={loading} onClick={handleSave} color="primary">
          {loading ? t('ui_elements.in_progress') : t('ssid_monitoring.edit_modal.save')}
        </Button>
      </Stack>
    </Modal>
  )
}

export default EditSsidModal
